import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Scan, MapPin, Package, ArrowRight, ArrowRightLeft, CheckCircle, AlertTriangle, RefreshCw, Box, Navigation, QrCode, Target, History, Trash2, X, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as authApi from '@/services/authApi';

const DesignTransferPicker = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const slipNumber = searchParams.get('slip');
  
  const scanInputRef = useRef(null);
  const destInputRef = useRef(null);
  
  const [user, setUser] = useState(null);
  const [staffId, setStaffId] = useState('');
  const [slip, setSlip] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Scanning states - BATCH MODE
  const [phase, setPhase] = useState('fetch'); // fetch, pick, place, complete, summary
  const [scannedUSN, setScannedUSN] = useState('');
  const [sourceBin, setSourceBin] = useState('');
  const [destinationBin, setDestinationBin] = useState('');
  const [pickResult, setPickResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // BATCH: Collected USNs for batch transfer
  const [collectedUSNs, setCollectedUSNs] = useState([]);
  const [transferSummary, setTransferSummary] = useState(null);
  
  // Progress
  const [progress, setProgress] = useState(null);
  
  // History
  const [showHistory, setShowHistory] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  
  // Skip dialog
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipRemarks, setSkipRemarks] = useState('');
  const [skipProcessing, setSkipProcessing] = useState(false);
  
  // Skipped items section
  const [showSkippedItems, setShowSkippedItems] = useState(false);
  const [skippedItems, setSkippedItems] = useState([]);
  const [revokeProcessing, setRevokeProcessing] = useState(null); // item_index being processed
  
  useEffect(() => {
    initializePicker();
  }, []);
  
  useEffect(() => {
    if (staffId && slipNumber) {
      fetchSlipAndItem();
    }
  }, [staffId, slipNumber]);
  
  useEffect(() => {
    // Auto-focus based on phase
    if (phase === 'pick' && scanInputRef.current) {
      scanInputRef.current.focus();
    } else if (phase === 'place' && destInputRef.current) {
      destInputRef.current.focus();
    }
  }, [phase]);
  
  const initializePicker = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
      setStaffId(userData.staff_id || userData.user_id || 'STAFF001');
    } catch (error) {
      console.error('Error fetching user:', error);
      setStaffId('STAFF001');
    }
  };
  
  const fetchSlipAndItem = async () => {
    setLoading(true);
    try {
      // First start the slip if not started
      try {
        await apiClient.post(`/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/start`, {
          staff_id: staffId
        });
      } catch (e) {
        // Might already be started
      }
      
      // Get slip details
      const slipRes = await apiClient.get(`/wms/fg/design-transfer-slip-detail`, { params: { slip_number: slipNumber } });
      setSlip(slipRes.data);
      
      // Extract skipped items from the slip
      const skipped = (slipRes.data.items || [])
        .map((item, index) => ({ ...item, item_index: index }))
        .filter(item => item.status === 'SKIPPED');
      setSkippedItems(skipped);
      
      // Get next item
      const itemRes = await apiClient.get(
        `/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/next-item?staff_id=${staffId}`
      );
      
      if (itemRes.data.all_items_completed) {
        setPhase('complete');
        setCurrentItem(null);
      } else {
        setCurrentItem(itemRes.data);
        setSourceBin(itemRes.data.suggested_bin || '');
        setPhase('pick');
      }
      
      // Get progress
      const progressRes = await apiClient.get(`/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/progress`);
      setProgress(progressRes.data);

      // RESUME: restore pieces already picked (but not yet placed) so an
      // accidental refresh doesn't lose the picker's collected list.
      try {
        const collectedRes = await apiClient.get(
          `/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/collected-usns`
        );
        const restored = collectedRes.data?.collected_usns || [];
        setCollectedUSNs(restored);
        if (restored.length > 0) {
          setPhase('pick');
          if (!destinationBin && restored[0]?.suggested_destination_bin) {
            setDestinationBin(restored[0].suggested_destination_bin);
          }
        }
      } catch (e) {
        // non-fatal — resume is best-effort
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load slip');
    } finally {
      setLoading(false);
    }
  };
  
  // BATCH: Add USN to collection (scan-pick without immediate destination)
  const handleScanUSN = async () => {
    if (!scannedUSN.trim()) {
      toast.error('Please scan a USN');
      return;
    }
    
    if (!sourceBin.trim()) {
      toast.error('Please enter source bin');
      return;
    }
    
    // Check if already scanned
    if (collectedUSNs.some(u => u.usn_code === scannedUSN.trim().toUpperCase())) {
      toast.error('This USN is already scanned');
      setScannedUSN('');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await apiClient.post(
        `/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/scan-pick`,
        {
          usn_code: scannedUSN.trim().toUpperCase(),
          source_bin: sourceBin.trim().toUpperCase(),
          staff_id: staffId
        }
      );
      
      // Add to collected USNs list
      setCollectedUSNs(prev => [...prev, {
        usn_code: response.data.usn_code,
        source_barcode: response.data.source_barcode,
        target_barcode: response.data.target_barcode,
        source_design: response.data.source_design,
        target_design: response.data.target_design,
        picked_from_bin: response.data.picked_from_bin,
        suggested_destination_bin: response.data.suggested_destination_bin
      }]);
      
      // Store suggested destination if not already set
      if (!destinationBin && response.data.suggested_destination_bin) {
        setDestinationBin(response.data.suggested_destination_bin);
      }
      
      toast.success(`USN ${scannedUSN} added to batch (${collectedUSNs.length + 1} items)`);
      setScannedUSN('');
      
      // Focus back on scan input
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan USN');
    } finally {
      setProcessing(false);
    }
  };
  
  // BATCH: Remove USN from collection
  const handleRemoveUSN = async (usnToRemove) => {
    try {
      // Call API to revert the USN back to STORED status
      await apiClient.post(
        `/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/cancel-pick`,
        {
          usn_code: usnToRemove.usn_code,
          staff_id: staffId
        }
      );
      
      setCollectedUSNs(prev => prev.filter(u => u.usn_code !== usnToRemove.usn_code));
      toast.success(`USN ${usnToRemove.usn_code} removed from batch`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove USN');
    }
  };
  
  // BATCH: Proceed to destination scanning phase
  const handleProceedToDestination = () => {
    if (collectedUSNs.length === 0) {
      toast.error('Please scan at least one USN first');
      return;
    }
    setPhase('place');
  };
  
  // BATCH: Place all collected USNs to destination
  const handlePlaceBatch = async () => {
    // 2026-05-25 — Branch the destination-bin requirement on the
    // slip's `direct_to_packing` flag. DTP slips skip bin placement
    // entirely; the backend ignores `destination_bin` when both
    // `direct_to_packing=true` AND `tagged_fg_pick_slip` is set
    // (see batch_place_design_transfer line 19845-19856). We send
    // a sentinel so the request body validator on the frontend
    // stays happy without confusing the operator with a bin scan
    // they shouldn't be doing.
    const isDtp = !!(slip?.direct_to_packing && slip?.tagged_fg_pick_slip);

    if (!isDtp && !destinationBin.trim()) {
      toast.error('Please enter or scan destination bin');
      return;
    }
    
    if (collectedUSNs.length === 0) {
      toast.error('No USNs to transfer');
      return;
    }
    
    setProcessing(true);
    try {
      // Call batch transfer endpoint
      const response = await apiClient.post(
        `/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/batch-place`,
        {
          usn_codes: collectedUSNs.map(u => u.usn_code),
          // DTP slips: the backend ignores destination_bin; send a
          // sentinel so server-side validation still passes.
          destination_bin: isDtp ? 'DTP_DIRECT_TO_PACKING' : destinationBin.trim().toUpperCase(),
          staff_id: staffId
        }
      );
      
      // Add to history
      collectedUSNs.forEach(item => {
        setTransferHistory(prev => [{
          usn_code: item.usn_code,
          from_barcode: item.source_barcode,
          to_barcode: item.target_barcode,
          from_bin: item.picked_from_bin,
          to_bin: isDtp
            ? `(direct to packing: ${slip?.tagged_fg_pick_slip || ''})`
            : destinationBin,
          timestamp: new Date()
        }, ...prev]);
      });

      // Show summary
      setTransferSummary({
        transferred_count: response.data.transferred_count,
        destination_bin: isDtp ? 'DTP_DIRECT_TO_PACKING' : destinationBin,
        target_barcode: collectedUSNs[0]?.target_barcode,
        target_design: collectedUSNs[0]?.target_design,
        slip_completed: response.data.slip_completed,
        handover_staff: response.data.handover_staff
      });

      toast.success(
        isDtp
          ? `Sent ${response.data.transferred_count} pieces to packing for ${slip?.tagged_fg_pick_slip || ''}`
          : `Transferred ${response.data.transferred_count} pieces to ${destinationBin}`
      );
      
      // Reset for next batch
      setCollectedUSNs([]);
      setScannedUSN('');
      setDestinationBin('');
      
      // Show summary phase or complete
      if (response.data.slip_completed) {
        setPhase('complete');
        toast.success('All items transferred! Slip completed.');
      } else {
        setPhase('summary');
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer batch');
    } finally {
      setProcessing(false);
    }
  };
  
  // Continue after summary to scan more
  const handleContinueScanning = async () => {
    setTransferSummary(null);
    await fetchSlipAndItem();
  };
  
  // Skip current item (when no stock available)
  const handleSkipItem = async () => {
    if (!skipRemarks.trim()) {
      toast.error('Please provide a reason for skipping');
      return;
    }
    
    if (!currentItem) {
      toast.error('No item to skip');
      return;
    }
    
    setSkipProcessing(true);
    try {
      await apiClient.post(
        `/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/skip-item`,
        {
          item_index: currentItem.item_index,
          remarks: skipRemarks.trim(),
          staff_id: staffId
        }
      );
      
      toast.success('Item skipped successfully');
      setSkipDialogOpen(false);
      setSkipRemarks('');
      
      // Fetch next item
      await fetchSlipAndItem();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip item');
    } finally {
      setSkipProcessing(false);
    }
  };
  
  // Revoke skip - make skipped item available for picking again
  const handleRevokeSkip = async (itemIndex) => {
    setRevokeProcessing(itemIndex);
    try {
      await apiClient.post(
        `/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/revoke-skip`,
        {
          item_index: itemIndex,
          staff_id: staffId
        }
      );
      
      toast.success('Skip revoked - item is now available for picking');
      setShowSkippedItems(false);
      
      // Refresh slip and items
      await fetchSlipAndItem();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke skip');
    } finally {
      setRevokeProcessing(null);
    }
  };
  
  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/wms/fg/design-transfer-slips')} 
              className="text-[#F9FAFB] hover:bg-white/10 p-2"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">Design Transfer</h1>
              <p className="text-xs text-[#E8DCC8]">Slip: {slipNumber}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Skipped Items Button - only show if there are skipped items */}
            {skippedItems.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSkippedItems(true)}
                className="text-orange-400 hover:bg-white/10 relative"
                data-testid="show-skipped-btn"
              >
                <SkipForward className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {skippedItems.length}
                </span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(true)}
              className="text-[#E8DCC8] hover:bg-white/10"
            >
              <History className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchSlipAndItem}
              className="text-[#E8DCC8] hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Progress Bar */}
      {progress && (
        <div className="bg-white border-b px-4 py-2">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Progress value={progress.progress_percentage} className="flex-1 h-2" />
            <span className="text-sm font-medium text-gray-600">
              {progress.transferred_qty}/{progress.total_qty} ({progress.progress_percentage}%)
            </span>
          </div>
        </div>
      )}
      
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Complete State */}
        {phase === 'complete' && (
          <Card className="border-2 border-green-300 bg-green-50">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-green-800 mb-2">Transfer Complete!</h2>
              <p className="text-green-600 mb-6">All items in slip {slipNumber} have been transferred.</p>
              {transferSummary?.handover_staff && (
                <div
                  className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-emerald-300 text-emerald-800"
                  data-testid="complete-handover-staff"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  Hand over the relabeled pieces to:{' '}
                  <span className="font-semibold">{transferSummary.handover_staff}</span>
                </div>
              )}
              <div>
              <Button onClick={() => navigate('/wms/fg/design-transfer-slips')} className="bg-green-600 hover:bg-green-700">
                Back to Slips
              </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Pick Phase */}
        {phase === 'pick' && currentItem && (
          <>
            {/* Current Item Info */}
            <Card className="mb-4 border-2 border-blue-200" data-testid="current-item-card">
              <CardHeader className="bg-blue-50 pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    Pick Item #{currentItem.item_index + 1}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkipDialogOpen(true)}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    data-testid="skip-item-btn"
                  >
                    <SkipForward className="w-4 h-4 mr-1" />
                    Skip Item
                  </Button>
                </div>
                <CardDescription>
                  Transfer: {currentItem.remaining_qty} pieces remaining
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Source */}
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-xs text-orange-600 font-medium mb-1">FROM (Source)</p>
                    <p className="font-mono font-bold text-lg">{currentItem.source_barcode}</p>
                    <p className="font-medium">{currentItem.source_design_name}</p>
                    <p className="text-sm text-gray-600">{currentItem.source_shade} | {currentItem.source_size}</p>
                  </div>
                  
                  {/* Target */}
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-xs text-green-600 font-medium mb-1">TO (Target)</p>
                    <p className="font-mono font-bold text-lg">{currentItem.target_barcode}</p>
                    <p className="font-medium">{currentItem.target_design_name}</p>
                    <p className="text-sm text-gray-600">{currentItem.target_shade}</p>
                  </div>
                </div>
                
                {/* Available Qty */}
                <div className="mt-3 p-2 bg-gray-50 rounded flex items-center justify-between">
                  <span className="text-sm text-gray-600">Available at source:</span>
                  <Badge variant="outline" className={currentItem.total_available >= currentItem.remaining_qty ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>
                    {currentItem.total_available} pieces
                  </Badge>
                </div>
              </CardContent>
            </Card>
            
            {/* Source Bin Selection */}
            <Card className="mb-4 border-2 border-amber-200" data-testid="source-bin-card">
              <CardHeader className="bg-amber-50 pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-amber-600" />
                  Step 1: Go to Source Bin
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {currentItem.source_locations?.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 mb-2">Select source bin:</p>
                    <div className="flex flex-wrap gap-2">
                      {currentItem.source_locations.slice(0, 5).map((loc, idx) => (
                        <Button
                          key={idx}
                          variant={sourceBin === loc.bin_code ? 'default' : 'outline'}
                          onClick={() => setSourceBin(loc.bin_code)}
                          className={sourceBin === loc.bin_code ? 'bg-amber-600' : ''}
                        >
                          <MapPin className="w-4 h-4 mr-1" />
                          {loc.bin_code}
                          <Badge variant="secondary" className="ml-2">{loc.available_qty}</Badge>
                        </Button>
                      ))}
                    </div>
                    <Input
                      value={sourceBin}
                      onChange={(e) => setSourceBin(e.target.value.toUpperCase())}
                      placeholder="Or scan/enter bin manually..."
                      className="mt-2"
                    />
                  </div>
                ) : (
                  <Alert className="bg-red-50 border-red-200">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-800">No stock found</AlertTitle>
                    <AlertDescription className="text-red-600">
                      No items found in FG inventory for this barcode.
                    </AlertDescription>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSkipDialogOpen(true)}
                      className="mt-3 text-orange-600 border-orange-300 hover:bg-orange-50"
                    >
                      <SkipForward className="w-4 h-4 mr-1" />
                      Skip This Item
                    </Button>
                  </Alert>
                )}
              </CardContent>
            </Card>
            
            {/* Scan USN */}
            <Card className="mb-4 border-2 border-purple-200" data-testid="scan-usn-card">
              <CardHeader className="bg-purple-50 pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-purple-600" />
                  Step 2: Scan USNs from Bin (Batch Mode)
                </CardTitle>
                <CardDescription>
                  Scan all required USNs, then proceed to destination
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <Input
                  ref={scanInputRef}
                  value={scannedUSN}
                  onChange={(e) => setScannedUSN(e.target.value.toUpperCase())}
                  onKeyPress={(e) => handleKeyPress(e, handleScanUSN)}
                  placeholder="Scan USN barcode..."
                  className="h-14 text-xl text-center font-mono"
                  disabled={processing || !sourceBin}
                  autoFocus
                  data-testid="usn-input"
                />
                <Button
                  onClick={handleScanUSN}
                  disabled={processing || !scannedUSN || !sourceBin}
                  className="w-full mt-3 h-12 bg-purple-600 hover:bg-purple-700"
                  data-testid="scan-btn"
                >
                  {processing ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Scan className="w-5 h-5 mr-2" />
                  )}
                  Add to Batch
                </Button>
              </CardContent>
            </Card>
            
            {/* Collected USNs List */}
            {collectedUSNs.length > 0 && (
              <Card className="mb-4 border-2 border-green-300" data-testid="collected-usns-card">
                <CardHeader className="bg-green-50 pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-green-600" />
                    Collected USNs ({collectedUSNs.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="max-h-40 overflow-y-auto space-y-2 mb-4">
                    {collectedUSNs.map((item, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200"
                      >
                        <div>
                          <p className="font-mono text-sm font-medium">{item.usn_code}</p>
                          <p className="text-xs text-gray-600">
                            {item.source_design} → {item.target_design}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveUSN(item)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`remove-usn-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    onClick={handleProceedToDestination}
                    className="w-full h-12 bg-teal-600 hover:bg-teal-700"
                    data-testid="proceed-to-dest-btn"
                  >
                    <Target className="w-5 h-5 mr-2" />
                    Proceed to Destination ({collectedUSNs.length} items)
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
        
        {/* Place Phase - Batch Destination Scan */}
        {phase === 'place' && collectedUSNs.length > 0 && (
          <>
            {/* Batch Summary */}
            <Card className="mb-4 border-2 border-green-300 bg-green-50" data-testid="batch-summary-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-bold text-green-800">{collectedUSNs.length} USNs Ready for Transfer</p>
                    <p className="text-sm text-green-600">
                      {collectedUSNs[0]?.source_design} → {collectedUSNs[0]?.target_design}
                    </p>
                  </div>
                </div>
                
                {/* Show list of USNs */}
                <div className="max-h-32 overflow-y-auto space-y-1 mb-3">
                  {collectedUSNs.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm p-1 bg-white rounded">
                      <span className="font-mono">{item.usn_code}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveUSN(item)}
                        className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPhase('pick')}
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Add More USNs
                </Button>
              </CardContent>
            </Card>
            
            {/* Destination — branches on slip.direct_to_packing.
                Regular: scan/enter destination bin.
                DTP: show a confirm panel that ships the pieces
                straight to the tagged FG Pick Slip (no bin). */}
            {(slip?.direct_to_packing && slip?.tagged_fg_pick_slip) ? (
              <Card className="border-2 border-indigo-300" data-testid="dtp-confirm-card">
                <CardHeader className="bg-indigo-50 pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-indigo-800">
                    <Box className="w-5 h-5 text-indigo-600" />
                    Direct to Packing
                  </CardTitle>
                  <CardDescription className="text-indigo-700">
                    Pieces will fulfil FG Pick Slip{' '}
                    <span className="font-mono font-bold">{slip.tagged_fg_pick_slip}</span>{' '}
                    — no bin placement needed.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="rounded bg-indigo-50/40 p-3 text-sm text-indigo-900 mb-3">
                    <p>
                      <span className="font-semibold">{collectedUSNs.length}</span> piece
                      {collectedUSNs.length === 1 ? '' : 's'} will be sent directly to packing.
                      After this step the FG slip is ready for the packing station.
                    </p>
                  </div>
                  <Button
                    onClick={handlePlaceBatch}
                    disabled={processing}
                    className="w-full h-12 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="dtp-confirm-btn"
                  >
                    {processing ? (
                      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Box className="w-5 h-5 mr-2" />
                    )}
                    Send {collectedUSNs.length} {collectedUSNs.length === 1 ? 'Piece' : 'Pieces'} to Packing
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-teal-200" data-testid="dest-bin-card">
                <CardHeader className="bg-teal-50 pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-teal-600" />
                    Scan Destination Bin
                  </CardTitle>
                  {collectedUSNs[0]?.suggested_destination_bin && (
                    <CardDescription className="text-teal-700">
                      Suggested: {collectedUSNs[0].suggested_destination_bin}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="p-4">
                  {collectedUSNs[0]?.suggested_destination_bin && (
                    <Button
                      variant={destinationBin === collectedUSNs[0].suggested_destination_bin ? 'default' : 'outline'}
                      onClick={() => setDestinationBin(collectedUSNs[0].suggested_destination_bin)}
                      className={`w-full mb-3 ${destinationBin === collectedUSNs[0].suggested_destination_bin ? 'bg-teal-600' : ''}`}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Use Suggested: {collectedUSNs[0].suggested_destination_bin}
                    </Button>
                  )}

                  <Input
                    ref={destInputRef}
                    value={destinationBin}
                    onChange={(e) => setDestinationBin(e.target.value.toUpperCase())}
                    onKeyPress={(e) => handleKeyPress(e, handlePlaceBatch)}
                    placeholder="Scan or enter destination bin..."
                    className="h-14 text-xl text-center font-mono"
                    disabled={processing}
                    data-testid="dest-input"
                  />

                  <Button
                    onClick={handlePlaceBatch}
                    disabled={processing || !destinationBin}
                    className="w-full mt-3 h-12 bg-teal-600 hover:bg-teal-700"
                    data-testid="place-batch-btn"
                  >
                    {processing ? (
                      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Box className="w-5 h-5 mr-2" />
                    )}
                    Transfer All {collectedUSNs.length} Items
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
        
        {/* Transfer Summary Phase */}
        {phase === 'summary' && transferSummary && (
          <Card className="border-2 border-blue-300 bg-blue-50">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-blue-800 mb-2">Transfer Successful!</h2>
              <div className="text-blue-600 mb-6 space-y-2">
                <p className="text-3xl font-bold">{transferSummary.transferred_count} pieces</p>
                {transferSummary.destination_bin === 'DTP_DIRECT_TO_PACKING'
                  ? (
                    <p>
                      sent directly to packing for FG Pick Slip{' '}
                      <span className="font-mono font-bold">
                        {slip?.tagged_fg_pick_slip || '?'}
                      </span>
                    </p>
                  )
                  : (
                    <p>
                      transferred to bin{' '}
                      <span className="font-mono font-bold">{transferSummary.destination_bin}</span>
                    </p>
                  )}
                <p className="text-sm">New SKU: {transferSummary.target_barcode}</p>
              </div>
              <div className="flex gap-3 justify-center">
                <Button 
                  onClick={handleContinueScanning}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Continue Scanning
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/wms/fg/design-transfer-slips')}
                >
                  Back to Slips
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      
      {/* Skip Item Dialog */}
      <Dialog open={skipDialogOpen} onOpenChange={setSkipDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <SkipForward className="w-5 h-5" />
              Skip Item
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {currentItem && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  Skipping item #{currentItem.item_index + 1}
                </p>
                <p className="font-mono font-medium">{currentItem.source_barcode} → {currentItem.target_barcode}</p>
                <p className="text-sm text-gray-600">{currentItem.source_design_name}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Remaining: {currentItem.remaining_qty} pieces
                </p>
              </div>
            )}
            <div>
              <Label className="text-gray-700">
                Reason for Skipping <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={skipRemarks}
                onChange={(e) => setSkipRemarks(e.target.value)}
                placeholder="e.g., Stock unavailable, Item not found in bin..."
                className="mt-2 min-h-[80px]"
                data-testid="skip-remarks-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSkipDialogOpen(false);
                setSkipRemarks('');
              }}
              disabled={skipProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSkipItem}
              disabled={skipProcessing || !skipRemarks.trim()}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="confirm-skip-btn"
            >
              {skipProcessing ? 'Skipping...' : 'Confirm Skip'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Session History
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {transferHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No transfers yet</p>
            ) : (
              <div className="space-y-2">
                {transferHistory.map((item, idx) => (
                  <Card key={idx} className="bg-gray-50">
                    <CardContent className="p-3">
                      <p className="font-mono text-sm font-medium">{item.usn_code}</p>
                      <p className="text-xs text-gray-600">
                        {item.from_barcode} → {item.to_barcode}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.from_bin} → {item.to_bin}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {item.timestamp.toLocaleTimeString()}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Skipped Items Dialog */}
      <Dialog open={showSkippedItems} onOpenChange={setShowSkippedItems}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <SkipForward className="w-5 h-5" />
              Skipped Items ({skippedItems.length})
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {skippedItems.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No skipped items</p>
            ) : (
              <div className="space-y-3">
                {skippedItems.map((item, idx) => (
                  <Card key={idx} className="bg-orange-50 border-orange-200">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-orange-800">
                            Item #{item.item_index + 1}
                          </p>
                          <p className="font-mono text-sm">{item.source_barcode} → {item.target_barcode}</p>
                          <p className="text-xs text-gray-600">{item.source_design_name || item.source_design}</p>
                          <p className="text-xs text-gray-500">Qty: {item.qty}</p>
                          <div className="mt-2 p-2 bg-white rounded border border-orange-200">
                            <p className="text-xs text-gray-500">Reason:</p>
                            <p className="text-sm text-gray-700">{item.skip_remarks || 'No reason provided'}</p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Skipped by: {item.skipped_by}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRevokeSkip(item.item_index)}
                          disabled={revokeProcessing === item.item_index}
                          className="border-green-500 text-green-600 hover:bg-green-50 shrink-0"
                          data-testid={`revoke-skip-${item.item_index}`}
                        >
                          {revokeProcessing === item.item_index ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Revoke
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSkippedItems(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DesignTransferPicker;
